import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Building2, MapPin, Plus, ChefHat, Store, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewAudit = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSite, setSelectedSite] = useState('');
  const [showSiteDialog, setShowSiteDialog] = useState(false);
  const [newSite, setNewSite] = useState({ name: '', location: '', site_type: 'central_kitchen' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`);
      setSites(response.data);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSite = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API}/sites`, newSite);
      setSites([...sites, response.data]);
      setSelectedSite(response.data.id);
      setShowSiteDialog(false);
      toast.success('Site created successfully');
      setNewSite({ name: '', location: '', site_type: 'central_kitchen' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create site');
    } finally {
      setCreating(false);
    }
  };

  const handleStartAudit = async () => {
    if (!selectedSite || !selectedType) {
      toast.error('Please select a site and audit type');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/audits`, {
        site_id: selectedSite,
        audit_type: selectedType,
        auditor_name: user.name
      });
      toast.success('Audit created successfully');
      navigate(`/audits/${response.data.id}/conduct`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create audit');
    } finally {
      setCreating(false);
    }
  };

  const filteredSites = sites.filter(site => 
    !selectedType || site.site_type === selectedType
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="new-audit-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Chivo'] text-slate-900">New Audit</h1>
        <p className="text-slate-500 mt-1">Select the type of audit you want to conduct</p>
      </div>

      {/* Audit Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card 
          className={`cursor-pointer transition-all duration-200 ${
            selectedType === 'central_kitchen' 
              ? 'ring-2 ring-slate-900 shadow-lg' 
              : 'hover:shadow-md'
          }`}
          onClick={() => setSelectedType('central_kitchen')}
          data-testid="central-kitchen-card"
        >
          <div 
            className="h-40 bg-cover bg-center rounded-t-lg"
            style={{ 
              backgroundImage: `url(https://images.unsplash.com/photo-1760001553414-5634201efc36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMGludGVyaW9yJTIwc3RhaW5sZXNzJTIwc3RlZWwlMjBjbGVhbnxlbnwwfHx8fDE3NzAxMzQ1NzR8MA&ixlib=rb-4.1.0&q=85)` 
            }}
          />
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <ChefHat className="w-5 h-5 text-slate-600" />
              </div>
              <CardTitle className="text-xl font-['Chivo']">Central Kitchen Audit</CardTitle>
            </div>
            <CardDescription className="mt-2">
              Comprehensive hygiene and safety audit for main production facility. Includes 80+ checklist items covering staff hygiene, storage, food preparation, cleaning, and documentation.
            </CardDescription>
          </CardHeader>
        </Card>

        <Card 
          className={`cursor-pointer transition-all duration-200 ${
            selectedType === 'site' 
              ? 'ring-2 ring-slate-900 shadow-lg' 
              : 'hover:shadow-md'
          }`}
          onClick={() => setSelectedType('site')}
          data-testid="site-audit-card"
        >
          <div 
            className="h-40 bg-cover bg-center rounded-t-lg"
            style={{ 
              backgroundImage: `url(https://images.unsplash.com/photo-1764941869968-a0988907123d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwZGluaW5nJTIwcm9vbSUyMG1vZGVybiUyMGVtcHR5fGVufDB8fHx8MTc3MDEzNDU4MHww&ixlib=rb-4.1.0&q=85)` 
            }}
          />
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Store className="w-5 h-5 text-slate-600" />
              </div>
              <CardTitle className="text-xl font-['Chivo']">Site / Outlet Audit</CardTitle>
            </div>
            <CardDescription className="mt-2">
              Front-of-house and local kitchen safety checks. Streamlined checklist focusing on service area, staff hygiene, storage, cleaning, and basic documentation.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Site Selection */}
      {selectedType && (
        <Card className="animate-in fade-in-50" data-testid="site-selection-card">
          <CardHeader>
            <CardTitle className="text-lg font-['Chivo']">Select Site</CardTitle>
            <CardDescription>Choose the location for this audit</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Select value={selectedSite} onValueChange={setSelectedSite}>
                  <SelectTrigger className="form-input" data-testid="site-select">
                    <SelectValue placeholder="Select a site..." />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredSites.length > 0 ? (
                      filteredSites.map((site) => (
                        <SelectItem key={site.id} value={site.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span>{site.name}</span>
                            <span className="text-slate-400">- {site.location}</span>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>
                        No sites available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Dialog open={showSiteDialog} onOpenChange={setShowSiteDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2" data-testid="add-site-btn">
                    <Plus className="w-4 h-4" />
                    Add Site
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Site</DialogTitle>
                    <DialogDescription>Create a new site/location for auditing</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateSite} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="site-name" className="form-label">Site Name</Label>
                      <Input
                        id="site-name"
                        placeholder="e.g., Main Kitchen - Building A"
                        value={newSite.name}
                        onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                        className="form-input"
                        required
                        data-testid="new-site-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="site-location" className="form-label">Location</Label>
                      <Input
                        id="site-location"
                        placeholder="e.g., 123 Industrial Area, Mumbai"
                        value={newSite.location}
                        onChange={(e) => setNewSite({ ...newSite, location: e.target.value })}
                        className="form-input"
                        required
                        data-testid="new-site-location-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="site-type" className="form-label">Site Type</Label>
                      <Select 
                        value={newSite.site_type} 
                        onValueChange={(value) => setNewSite({ ...newSite, site_type: value })}
                      >
                        <SelectTrigger className="form-input" data-testid="new-site-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="central_kitchen">Central Kitchen</SelectItem>
                          <SelectItem value="site">Site / Outlet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setShowSiteDialog(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        className="btn-primary" 
                        disabled={creating}
                        data-testid="create-site-submit-btn"
                      >
                        {creating ? 'Creating...' : 'Create Site'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {selectedSite && (
              <div className="pt-4 border-t">
                <Button 
                  onClick={handleStartAudit} 
                  className="w-full btn-primary flex items-center justify-center gap-2"
                  disabled={creating}
                  data-testid="start-audit-btn"
                >
                  {creating ? 'Starting Audit...' : (
                    <>
                      Start Audit
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NewAudit;
